/**
 * UsersTab - Users management tab component
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { 
  RefreshCw, 
  AlertCircle,
  Loader2,
  Search,
  User,
  Eye,
  CreditCard,
  Download
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

import { API, formatEST } from "./utils";

export default function UsersTab({ onUserClick }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'email', 'created'

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users/all`);
      setUsers(response.data.users || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    let result = users.filter(user => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (user.full_name || '').toLowerCase().includes(searchLower) ||
        (user.email || '').toLowerCase().includes(searchLower) ||
        (user.phone || '').toLowerCase().includes(searchLower) ||
        (user.school || '').toLowerCase().includes(searchLower)
      );
    });
    
    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'email':
          return (a.email || '').localeCompare(b.email || '');
        case 'created':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        default:
          return (a.full_name || '').localeCompare(b.full_name || '');
      }
    });
    
    return result;
  }, [users, searchTerm, sortBy]);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-slate-600">{error}</p>
        <Button onClick={fetchUsers} variant="outline" size="sm" className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Users ({filteredUsers.length})
        </h2>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-[250px]"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="created">Newest First</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchUsers}>
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
          <Button
            data-testid="export-emails-csv-btn"
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/users/emails/export?format=csv`, { responseType: 'blob' });
                const blob = new Blob([res.data], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `customer_emails_${new Date().toISOString().slice(0,10)}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast.success("Email list downloaded");
              } catch (err) {
                toast.error(err.response?.data?.detail || err.message || "Export failed");
              }
            }}
            className="gap-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
          >
            <Download className="w-4 h-4" />
            Export Emails (CSV)
          </Button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>School/Club</TableHead>
              <TableHead>Stripe Customer</TableHead>
              <TableHead>Member Since</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                  {searchTerm ? 'No users matching search' : 'No users found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} className="hover:bg-slate-50">
                  <TableCell>
                    <div 
                      className="flex items-center gap-3 cursor-pointer hover:text-blue-600"
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey) {
                          window.open(`/users/${user.id}`, '_blank');
                        } else {
                          onUserClick && onUserClick(user.id, user.full_name);
                        }
                      }}
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="font-medium">{user.full_name || '-'}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <a href={`mailto:${user.email}`} className="text-blue-600 hover:underline text-sm">
                      {user.email || '-'}
                    </a>
                  </TableCell>
                  <TableCell className="text-sm">{user.phone || '-'}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {user.school && <div>{user.school}</div>}
                      {user.club_name && <div className="text-slate-500">{user.club_name}</div>}
                      {!user.school && !user.club_name && '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.stripe_customer_id ? (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-mono text-xs">
                        <CreditCard className="w-3 h-3 mr-1" />
                        {user.stripe_customer_id.slice(-8)}
                      </Badge>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {formatEST(user.created_at, 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey) {
                          window.open(`/users/${user.id}`, '_blank');
                        } else {
                          onUserClick && onUserClick(user.id, user.full_name);
                        }
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
